// Copyright (c) 2025 WiseTime. All rights reserved.

import { EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import {
  ListFavouriteTimesheetFieldOptionsRequest,
  ListFavouriteTimesheetFieldOptionsResponse,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { RootState } from "../../../app/store"

type ListFavouriteTimesheetFieldOptionsRpc = (siteId: string) => Observable<ListFavouriteTimesheetFieldOptionsResponse>

export const listFavouriteTimesheetFieldOptions = (listFavouriteTimesheetFieldOptionsRpc: ListFavouriteTimesheetFieldOptionsRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.listFavouriteTimesheetFieldOptions.match),
      withLatestFrom(state$),
      mergeMap(([_, { userContext: { currentSiteId }}]) => {
        if (!currentSiteId) {
          return EMPTY
        }
        return of(currentSiteId)
          .pipe(
            mergeMap(listFavouriteTimesheetFieldOptionsRpc),
            map(response => ({ siteId: currentSiteId, options: response.toObject().timesheetFieldOptionsList })),
            map(Slice.setFavouriteTimesheetFieldOptions),
            catchError(() => of(showSnackBar({
              message: t({
                id: "msg_list_favourite_timesheet_field_options_failed",
                message: "Failed to load favourite timesheet field options. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
              }),
              messageType: "ERROR",
            }))),
          )
      }),
    )

const listFavouriteTimesheetFieldOptionsRpc = (siteId: string): Observable<ListFavouriteTimesheetFieldOptionsResponse> =>
  grpc(() => timesheetFieldClient.listFavouriteTimesheetFieldOptions(new ListFavouriteTimesheetFieldOptionsRequest().setSiteId(siteId), null))

export default listFavouriteTimesheetFieldOptions(listFavouriteTimesheetFieldOptionsRpc)
